/**
 * Functions for fetching season details from the Peacock API
 */

import { SeasonNode } from "./types";

import { fetchAssetDetails } from "@/utils/FetchAssetDetails";
import { isSeries } from "@/utils/FetchAssetDetails/types";

/**
 * Fetches season details for a series from the Peacock API
 *
 * @remarks
 * Uses fetchAssetDetails with the represent parameter to fetch nested season data.
 * Seasons are extracted from the relationships.items.data array and sorted by season number.
 *
 * @param providerSeriesId - The series provider ID (from series.attributes.providerSeriesId)
 * @returns Promise that resolves to an array of season nodes with episode counts
 *
 * @example
 * ```typescript
 * // Fetch series first
 * const asset = await fetchAssetDetails(seriesId);
 *
 * // Then fetch seasons using providerSeriesId
 * fetchSeasonDetails(asset.attributes.providerSeriesId)
 *   .then(seasons => console.log(`Found ${seasons.length} seasons`));
 * ```
 *
 * @throws Will throw an error if the network request fails or no seasons are found
 */
const fetchSeasonDetails = async (
  providerSeriesId: string,
): Promise<SeasonNode[]> => {
  try {
    // Use fetchAssetDetails to get the series with nested season data
    const data = await fetchAssetDetails(
      `provider_series_id/${providerSeriesId}`,
      {
        represent: "(items(items))",
        contentSegments: "*",
        features: "upcoming",
      },
    );

    // Verify we got a series response with seasons
    if (!isSeries(data)) {
      throw new Error("Expected series response but got different type");
    }

    if (!data.relationships?.items?.data) {
      throw new Error("No seasons found in response");
    }

    const seasons = data.relationships.items.data;

    // Sort by season number
    seasons.sort(
      (a, b) => a.attributes.seasonNumber - b.attributes.seasonNumber,
    );

    return seasons;
  } catch (error) {
    console.error("Error fetching seasons:", error);
    throw error;
  }
};

export { fetchSeasonDetails };
